import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { BoundingBoxNode } from "features/canvas/types";
import {
  BoundingBoxesContextProps,
  BoundingBoxesContextType,
  StepsDataProps,
  ValidBoxLabelsProps,
  handleChangeStepsType,
} from "types/boundingBoxesContextTypes";
import { CanvasAnnotation } from "types/imageAnnotations";

export const BoundingBoxesContext = createContext<BoundingBoxesContextType | null>(null);

export function BoundingBoxesProvider({
  children,
  isBoundingBoxes = false,
  annotations: defaultAnnotations,
  canDraw = true,
}: BoundingBoxesContextProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [validBoxLabels, setValidBoxLabels] = useState<ValidBoxLabelsProps[]>([]);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [showAnnotations, setShowAnnotations] = useState<boolean>(true);
  const [stepsData, setStepsData] = useState<StepsDataProps>({
    currentStep: 0,
    totalSteps: 0,
  });
  const [annotations, setAnnotations] = useState<CanvasAnnotation[]>(defaultAnnotations ?? []);
  const [textRecognitionValue, setTextRecognitionValue] = useState<string>("");

  useEffect(() => {
    if (defaultAnnotations) {
      setAnnotations(defaultAnnotations);
    }
  }, [defaultAnnotations]);

  // method to save annotations when changing step or when user clicks
  // on submit button and updates the step data
  const handleChangeSteps: handleChangeStepsType = useCallback(
    (newStep: number) => {
      // update the annotations if is a valid step
      if (stepsData.currentStep <= validBoxLabels.length) {
        const updatedLabels = validBoxLabels.map((item, idx) =>
          stepsData.currentStep === idx + 1 ? { ...item, items: annotations } : item
        );
        setValidBoxLabels(updatedLabels);
      }
      // asign the new step
      setStepsData((data) => ({ ...data, currentStep: newStep }));
      // asign the annotations for the new step
      if (newStep <= validBoxLabels.length) {
        // if the new step is a valid step, asign the annotations from that step
        const annotations = validBoxLabels[newStep - 1]?.items || [];
        setAnnotations(annotations);
      } else {
        // in any other case, we dont want to show any annotations
        setAnnotations([]);
      }
    },
    [setStepsData, annotations, stepsData, validBoxLabels]
  );

  const getValidBoxes = useCallback(
    () =>
      validBoxLabels.map((label, i) => {
        if (stepsData.currentStep === i + 1) {
          return { ...label, items: [...annotations] };
        }
        return label;
      }),
    [annotations, stepsData.currentStep, validBoxLabels]
  );

  const [selectedAnnotation, setSelectedAnnotation] = useState<CanvasAnnotation>();
  const actualBox: ValidBoxLabelsProps | undefined = useMemo(() => {
    const getCurrentItem = validBoxLabels.find((_, idx) => idx + 1 === stepsData.currentStep);
    if (getCurrentItem) {
      return getCurrentItem;
    }
    return undefined;
  }, [validBoxLabels, stepsData.currentStep]);

  const [isSettingsExpanded, setIsSettingsExpanded] = useState<boolean>(false);

  const [hoveredNode, setHoveredNode] = useState<BoundingBoxNode>("none");
  const getBoundingBoxCursor = useCallback(
    (isRotated: boolean = false, flipX: boolean = false, flipY: boolean = false) => {
      const HORIZONTAL_HANDLES: BoundingBoxNode[] = ["left-middle", "right-middle"];
      const VERTICAL_HANDLES: BoundingBoxNode[] = ["top-middle", "bottom-middle"];

      if (HORIZONTAL_HANDLES.includes(hoveredNode)) {
        return isRotated ? "ns-resize" : "ew-resize";
      }

      if (VERTICAL_HANDLES.includes(hoveredNode)) {
        return isRotated ? "ew-resize" : "ns-resize";
      }

      const shouldCornerRotate = isRotated ? flipX === flipY : flipX !== flipY;

      if (hoveredNode === "top-left" || hoveredNode === "bottom-right") {
        return shouldCornerRotate ? "nesw-resize" : "nwse-resize";
      }

      if (hoveredNode === "bottom-left" || hoveredNode === "top-right") {
        return shouldCornerRotate ? "nwse-resize" : "nesw-resize";
      }

      if (hoveredNode === "center" || hoveredNode === "active-bounding-box") {
        return "move";
      }

      if (hoveredNode === "bounding-box" || hoveredNode === "close-icon") {
        return "default";
      }

      if (canDraw) {
        return "crosshair";
      }

      return "default";
    },
    [hoveredNode, canDraw]
  );

  const handleSetHoveredNode = useCallback(
    (node: BoundingBoxNode) => {
      if (isDragging) {
        return;
      }

      setHoveredNode(node);
    },
    [isDragging]
  );

  const memorizeValues = useMemo(
    () => ({
      selectedAnnotation,
      isDeleteModalOpen,
      validBoxLabels,
      stepsData,
      annotations,
      setValidBoxLabels,
      setStepsData,
      setAnnotations,
      handleChangeSteps,
      setIsDeleteModalOpen,
      showAnnotations,
      setShowAnnotations,
      actualBox,
      setSelectedAnnotation,
      getValidBoxes,
      fullScreen,
      setFullScreen,
      isBoundingBoxes,
      isSettingsExpanded,
      setIsSettingsExpanded,
      setHoveredNode: handleSetHoveredNode,
      hoveredNode,
      getBoundingBoxCursor,
      isDragging,
      setIsDragging,
      canDraw,
      textRecognitionValue,
      setTextRecognitionValue,
    }),
    [
      selectedAnnotation,
      isDeleteModalOpen,
      validBoxLabels,
      stepsData,
      annotations,
      setValidBoxLabels,
      setStepsData,
      setAnnotations,
      handleChangeSteps,
      setIsDeleteModalOpen,
      showAnnotations,
      setShowAnnotations,
      actualBox,
      setSelectedAnnotation,
      getValidBoxes,
      fullScreen,
      setFullScreen,
      isBoundingBoxes,
      isSettingsExpanded,
      setIsSettingsExpanded,
      handleSetHoveredNode,
      hoveredNode,
      getBoundingBoxCursor,
      isDragging,
      setIsDragging,
      canDraw,
      textRecognitionValue,
    ]
  );

  return <BoundingBoxesContext.Provider value={memorizeValues}>{children}</BoundingBoxesContext.Provider>;
}

export function useBoundingBoxesContext() {
  const context = useContext(BoundingBoxesContext);

  if (!context) {
    throw new Error("useBoundingBoxesContext must be used inside of BoundingBoxesProvider");
  }

  return context;
}
